import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, X, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const {
    items,
    isOpen,
    setIsOpen,
    removeItem,
    updateQty,
    subtotal,
    totalItems,
  } = useCart();
  const navigate = useNavigate();

  const freeShippingThreshold = 35;
  const remaining = Math.max(0, freeShippingThreshold - subtotal);
  const progress = Math.min(100, (subtotal / freeShippingThreshold) * 100);

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-left">
            <ShoppingBag className="h-5 w-5" />
            O teu carrinho ({totalItems})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-secondary flex items-center justify-center">
              <ShoppingBag className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">O teu carrinho está vazio</p>
              <p className="text-sm text-muted-foreground mt-1">
                Descobre os nossos dispositivos best-seller.
              </p>
            </div>
            <Button
              onClick={() => {
                setIsOpen(false);
                navigate("/loja");
              }}
              className="rounded-full"
            >
              Explorar loja
            </Button>
          </div>
        ) : (
          <>
            {/* Free shipping progress */}
            <div className="px-5 py-4 border-b border-border bg-secondary/50">
              <p className="text-xs text-muted-foreground mb-2">
                {remaining > 0 ? (
                  <>
                    Faltam <span className="font-semibold text-foreground">€ {remaining.toFixed(2)}</span> para envio grátis!
                  </>
                ) : (
                  <span className="font-semibold text-primary">Parabéns! Tens envio grátis 🎉</span>
                )}
              </p>
              <div className="h-1.5 rounded-full bg-border overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-20 w-20 rounded-md object-cover bg-secondary shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {item.brand}
                    </p>
                    <p className="text-sm font-medium leading-snug line-clamp-2">
                      {item.name}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-border rounded-full">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="h-7 w-7 flex items-center justify-center hover:text-primary"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="h-7 w-7 flex items-center justify-center hover:text-primary"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="text-sm font-semibold">
                        € {(item.price * item.qty).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-muted-foreground hover:text-destructive self-start"
                    aria-label="Remover"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-5 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold text-lg">€ {subtotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Impostos e portes calculados no checkout.
              </p>
              <Button onClick={goCheckout} className="w-full rounded-full h-12 text-base">
                Finalizar compra
              </Button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-sm text-muted-foreground hover:text-foreground py-1"
              >
                Continuar a comprar
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
